import { 
  auth, 
  db, 
  collection, 
  query, 
  where, 
  getDocs, 
  getDoc, 
  doc, 
  onAuthStateChanged 
} from '@/firebase'

export interface TutorProfile {
  uid: string
  email: string
  firstName: string
  middleName?: string
  lastName: string
  suffix?: string
  subjects: string[] | string | null | undefined
  educationLevel: string
  fieldOfStudy: string
  institution: string
  experienceYears: number
  profileStatus: string
  applicationStatus: string
  isVerified: boolean
  avatar?: string
  isOnline?: boolean
}

export interface StudentProfile {
  uid: string
  email: string
  firstName: string
  middleName?: string
  lastName: string
  suffix?: string
  userType: string
  isVerified: boolean
  avatar?: string
}

/**
 * Fetch all approved tutors from Firebase
 */
export async function fetchApprovedTutors(): Promise<TutorProfile[]> {
  try {
    const tutorsQuery = query(
      collection(db, 'users'),
      where('userType', '==', 'tutor'),
      where('profileStatus', '==', 'approved'),
      where('applicationStatus', '==', 'approved'),
      where('isVerified', '==', true),
      where('status', '==', 'active')
    )
    
    const querySnapshot = await getDocs(tutorsQuery)
    const tutors: TutorProfile[] = []
    
    querySnapshot.forEach((doc) => {
      const data = doc.data()
      try {
        tutors.push({
          uid: doc.id,
          email: data.email || '',
          firstName: data.firstName || '',
          middleName: data.middleName,
          lastName: data.lastName || '',
          suffix: data.suffix,
          subjects: data.subjects, // Keep as is - will be handled by formatTutorSubjects
          educationLevel: data.educationLevel || '',
          fieldOfStudy: data.fieldOfStudy || '',
          institution: data.institution || '',
          experienceYears: data.experienceYears || 0,
          profileStatus: data.profileStatus || '',
          applicationStatus: data.applicationStatus || '',
          isVerified: data.isVerified || false,
          avatar: data.avatar,
          isOnline: false // Will be updated by CometChat presence
        })
      } catch (error) {
        console.warn('Error processing tutor data for doc:', doc.id, error)
      }
    })
    
    return tutors
  } catch (error) {
    console.error('Error fetching approved tutors:', error)
    return []
  }
}

/**
 * Get current user's profile data
 */
export async function getCurrentUserProfile(): Promise<StudentProfile | TutorProfile | null> {
  return new Promise((resolve) => {
    onAuthStateChanged(auth, async (user) => {
      if (!user) {
        resolve(null)
        return
      }
      
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid))
        if (userDoc.exists()) {
          const data = userDoc.data()
          resolve({
            uid: user.uid,
            email: data.email,
            firstName: data.firstName,
            middleName: data.middleName,
            lastName: data.lastName,
            suffix: data.suffix,
            userType: data.userType,
            isVerified: data.isVerified,
            avatar: data.avatar,
            ...(data.userType === 'tutor' && {
              subjects: data.subjects || [],
              educationLevel: data.educationLevel,
              fieldOfStudy: data.fieldOfStudy,
              institution: data.institution,
              experienceYears: data.experienceYears,
              profileStatus: data.profileStatus,
              applicationStatus: data.applicationStatus,
            })
          })
        } else {
          resolve(null)
        }
      } catch (error) {
        console.error('Error fetching user profile:', error)
        resolve(null)
      }
    })
  })
}

/**
 * Format tutor display name
 */
export function formatTutorName(tutor: TutorProfile): string {
  const parts = [tutor.firstName]
  // Exclude middle name for consistency
  parts.push(tutor.lastName)
  if (tutor.suffix) parts.push(tutor.suffix)
  return parts.join(' ')
}

/**
 * Format tutor subjects for display
 */
export function formatTutorSubjects(tutor: TutorProfile): string {
  // Handle case where subjects might be undefined, null, or not an array
  if (!tutor.subjects) {
    return tutor.fieldOfStudy || 'General'
  }
  
  // If subjects is a string, return it directly
  if (typeof tutor.subjects === 'string') {
    return tutor.subjects
  }
  
  // If subjects is an array
  if (Array.isArray(tutor.subjects)) {
    if (tutor.subjects.length === 0) {
      return tutor.fieldOfStudy || 'General'
    }
    return tutor.subjects.join(', ')
  }
  
  // Fallback for any other data type
  return tutor.fieldOfStudy || 'General'
}

/**
 * Fetch all verified students from Firebase
 */
export async function fetchVerifiedStudents(): Promise<StudentProfile[]> {
  try {
    const studentsQuery = query(
      collection(db, 'users'),
      where('userType', '==', 'student'),
      where('isVerified', '==', true)
    )
    
    const querySnapshot = await getDocs(studentsQuery)
    const students: StudentProfile[] = []
    
    querySnapshot.forEach((doc) => {
      const data = doc.data()
      students.push({
        uid: doc.id,
        email: data.email,
        firstName: data.firstName,
        middleName: data.middleName,
        lastName: data.lastName,
        suffix: data.suffix,
        userType: data.userType,
        isVerified: data.isVerified,
        avatar: data.avatar
      })
    })
    
    return students
  } catch (error) {
    console.error('Error fetching verified students:', error)
    return []
  }
}

/**
 * Format student display name
 */
export function formatStudentName(student: StudentProfile): string {
  const parts = [student.firstName]
  if (student.middleName) parts.push(student.middleName)
  parts.push(student.lastName)
  if (student.suffix) parts.push(student.suffix)
  return parts.join(' ')
}
